import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Info,
  LineChart as LineIcon,
  Minimize2,
  PieChart as PieIcon,
  TrendingUp,
} from "lucide-react";
import {
  SWITCHABLE_KINDS,
  type ChartInsight,
  type ChartKind,
  type ChartSpec,
} from "@/lib/chartSpec";

type Props = {
  spec: ChartSpec;
};

const PALETTE = [
  "#0ea5e9", // sky-500
  "#6366f1", // indigo-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#ef4444", // red-500
  "#8b5cf6", // violet-500
  "#06b6d4", // cyan-500
  "#84cc16", // lime-500
];

/**
 * Renders an analytic chart + a "Top Insights" sidebar. Mirrors the layout
 * of IBM Cognos's @Cognos assistant: chart on the left with chart-type
 * tabs above it, insights stacked on the right.
 */
export function ChartBlock({ spec }: Props) {
  const [kind, setKind] = useState<ChartKind>(spec.kind);
  return (
    <figure className="overflow-hidden rounded-lg border border-datumcraft-slate/25 bg-white">
      <header className="flex items-center justify-between gap-2 border-b border-datumcraft-slate/15 px-3 py-2">
        <div className="min-w-0">
          <p className="text-2xs font-medium uppercase tracking-wide text-datumcraft-slate">Chart</p>
          <p className="truncate text-sm font-semibold text-datumcraft-navy">{spec.title}</p>
        </div>
        <ChartKindTabs current={kind} onChange={setKind} />
      </header>
      <div className="grid grid-cols-1 gap-3 p-3 md:grid-cols-[minmax(0,1fr)_260px]">
        <div className="h-72 min-w-0">
          <ChartCanvas spec={spec} kind={kind} />
        </div>
        <InsightsPanel insights={spec.insights} />
      </div>
    </figure>
  );
}

function ChartKindTabs({
  current,
  onChange,
}: {
  current: ChartKind;
  onChange: (k: ChartKind) => void;
}) {
  return (
    <div role="tablist" className="flex shrink-0 rounded-md border border-datumcraft-slate/30 bg-datumcraft-fog p-0.5">
      {SWITCHABLE_KINDS.map((k) => (
        <button
          key={k}
          role="tab"
          aria-selected={current === k}
          type="button"
          onClick={() => onChange(k)}
          title={kindLabel(k)}
          className={`inline-flex h-7 w-7 items-center justify-center rounded text-datumcraft-steel transition-colors ${
            current === k
              ? "bg-white text-datumcraft-navy shadow-sm"
              : "hover:text-datumcraft-navy"
          }`}
        >
          <KindIcon kind={k} />
        </button>
      ))}
    </div>
  );
}

function KindIcon({ kind }: { kind: ChartKind }) {
  if (kind === "line") return <LineIcon size={14} aria-hidden />;
  if (kind === "area") return <TrendingUp size={14} aria-hidden />;
  if (kind === "bar" || kind === "column") return <BarChart3 size={14} aria-hidden />;
  if (kind === "pie") return <PieIcon size={14} aria-hidden />;
  return <Minimize2 size={14} aria-hidden />;
}

function kindLabel(k: ChartKind): string {
  return k.charAt(0).toUpperCase() + k.slice(1);
}

function ChartCanvas({ spec, kind }: { spec: ChartSpec; kind: ChartKind }) {
  const xKey = spec.x.column;
  const yKey = spec.y.column;
  const seriesKey = spec.series?.column;

  // Pivot when a series column is supplied so each series renders as its
  // own line/bar. Recharts wants {x, seriesA: y, seriesB: y, ...} rows.
  const { rows, seriesNames } = useMemo(
    () => pivotForSeries(spec.data, xKey, yKey, seriesKey),
    [spec.data, xKey, yKey, seriesKey],
  );

  if (!rows.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-datumcraft-slate">
        No chartable rows.
      </div>
    );
  }

  if (kind === "pie") {
    const pieData = rows.map((r) => ({
      name: String(r[xKey] ?? ""),
      value: toNumber(r[yKey]) ?? 0,
    }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip />
          <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
            {pieData.map((_, i) => (
              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (kind === "scatter") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Scatter data={rows} fill={PALETTE[0]} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  if (kind === "area") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          {seriesNames.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {seriesNames.map((name, i) => (
            <Area
              key={name}
              type="monotone"
              dataKey={name}
              stroke={PALETTE[i % PALETTE.length]}
              fill={PALETTE[i % PALETTE.length]}
              fillOpacity={0.25}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (kind === "bar" || kind === "column") {
    // Both render as a vertical bar chart in Recharts; "bar" vs "column" is
    // a Cognos-ism we keep for tab parity but we draw the same shape. A
    // future refactor could swap to layout="vertical" for "bar".
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          {seriesNames.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {seriesNames.map((name, i) => (
            <Bar key={name} dataKey={name} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Default: line
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        {seriesNames.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {seriesNames.map((name, i) => (
          <Line
            key={name}
            type="monotone"
            dataKey={name}
            stroke={PALETTE[i % PALETTE.length]}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function InsightsPanel({ insights }: { insights: ChartInsight[] }) {
  if (!insights || insights.length === 0) {
    return (
      <aside className="flex h-full flex-col rounded-md border border-datumcraft-slate/20 bg-datumcraft-fog/50 p-3 text-xs text-datumcraft-slate">
        <p className="mb-1 font-semibold text-datumcraft-navy">Top Insights</p>
        <p>No insights detected for this chart.</p>
      </aside>
    );
  }
  return (
    <aside className="flex h-full flex-col rounded-md border border-datumcraft-slate/20 bg-datumcraft-fog/50 p-3">
      <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-datumcraft-navy">
        Top Insights
      </p>
      <ul className="space-y-2 overflow-y-auto pr-1 text-[12px] text-datumcraft-ink">
        {insights.map((ins, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className={severityClass(ins.severity)}>
              <InsightIcon insight={ins} />
            </span>
            <span className="leading-snug">{ins.text}</span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function InsightIcon({ insight }: { insight: ChartInsight }) {
  if (insight.severity !== "info") return <AlertTriangle size={12} aria-hidden />;
  if (insight.kind === "trend") return <TrendingUp size={12} aria-hidden />;
  if (insight.kind === "max") return <ArrowUpRight size={12} aria-hidden />;
  if (insight.kind === "min") return <ArrowDownRight size={12} aria-hidden />;
  if (insight.kind === "delta") return <TrendingUp size={12} aria-hidden />;
  return <Info size={12} aria-hidden />;
}

function severityClass(s: ChartInsight["severity"]) {
  if (s === "danger") return "mt-0.5 text-datumcraft-danger";
  if (s === "warn") return "mt-0.5 text-amber-600";
  return "mt-0.5 text-datumcraft-accentDark";
}

function pivotForSeries(
  data: Array<Record<string, unknown>>,
  xKey: string,
  yKey: string,
  seriesKey: string | undefined,
): { rows: Array<Record<string, unknown>>; seriesNames: string[] } {
  if (!seriesKey) {
    return { rows: data, seriesNames: [yKey] };
  }
  const byX = new Map<unknown, Record<string, unknown>>();
  const seriesSet = new Set<string>();
  for (const row of data) {
    const xv = row[xKey];
    const sv = String(row[seriesKey] ?? "");
    seriesSet.add(sv);
    if (!byX.has(xv)) byX.set(xv, { [xKey]: xv });
    byX.get(xv)![sv] = row[yKey];
  }
  return { rows: Array.from(byX.values()), seriesNames: Array.from(seriesSet) };
}

function toNumber(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
